import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  UserPlus, Mail, Clock, CheckCircle2, XCircle, RefreshCw,
  Send, MoreVertical, Trash2, RotateCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role labels mapping
const ROLE_LABELS = {
  director: 'Director',
  bd_manager: 'Business Development Manager',
  senior_engineer: 'Senior Engineer',
  associate_engineer: 'Associate Engineer',
  engineer: 'Engineer'
};

const ROLE_OPTIONS = [
  { value: 'director', label: 'Director' },
  { value: 'bd_manager', label: 'Business Development Manager' },
  { value: 'senior_engineer', label: 'Senior Engineer' },
  { value: 'associate_engineer', label: 'Associate Engineer' },
  { value: 'engineer', label: 'Engineer' }
];

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  revoked: 'bg-red-100 text-red-700',
  expired: 'bg-slate-100 text-slate-500'
};

export const InviteManagement = () => {
  const { token } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'engineer'
  });

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    fetchInvites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invites`, { headers });
      const data = await response.json();
      setInvites(data.invites || []);
    } catch (err) {
      console.error('Failed to fetch invites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const response = await fetch(`${API_URL}/api/invites`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send invite');
      }

      toast.success(`Invite sent to ${formData.email}`);
      setFormData({ email: '', first_name: '', last_name: '', role: 'engineer' });
      setShowForm(false);
      fetchInvites();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/invites/${inviteId}/resend`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to resend');
      }

      toast.success('Invite resent successfully');
      fetchInvites();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRevoke = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/invites/${inviteId}/revoke`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to revoke');
      }

      toast.success('Invite revoked');
      fetchInvites();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (inviteId) => {
    if (!confirm('Are you sure you want to permanently delete this invite from history?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/invites/${inviteId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete');
      }

      toast.success('Invite deleted from history');
      fetchInvites();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const otherInvites = invites.filter(i => i.status !== 'pending');

  return (
    <div className="space-y-6" data-testid="invite-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-[#1e3a5f]" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-[#1e3a5f]">Team Invites</h2>
            <p className="text-slate-500 text-sm">Invite new members to your workspace</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchInvites}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className="bg-[#1e3a5f] hover:bg-[#162c46]"
            data-testid="new-invite-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            New Invite
          </Button>
        </div>
      </div>

      {/* New Invite Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 animate-fade-in">
          <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Send New Invite</h3>
          <form onSubmit={handleSendInvite} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="colleague@company.com"
                    className="pl-10"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Role</Label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm"
                  data-testid="invite-role-select"
                >
                  {ROLE_OPTIONS.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">First Name (optional)</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Last Name (optional)</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Smith"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={sending}
                className="bg-[#1e3a5f] hover:bg-[#162c46]"
                data-testid="send-invite-btn"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Invite
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-amber-50/50">
            <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              Pending Invites ({pendingInvites.length})
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{invite.email}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-slate-500 text-sm">{ROLE_LABELS[invite.role] || invite.role}</span>
                      <span className="text-slate-300">•</span>
                      <span className="text-slate-400 text-sm">Expires {formatDate(invite.expires_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS.pending}`}>
                    Pending
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleResend(invite.id)}>
                        <RotateCw className="w-4 h-4 mr-2" />
                        Resend Invite
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleRevoke(invite.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Revoke Invite
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other Invites (Accepted, Revoked, Expired) */}
      {otherInvites.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-[#1e3a5f]">
              Invite History ({otherInvites.length})
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {otherInvites.map((invite) => (
              <div key={invite.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    invite.status === 'accepted' ? 'bg-emerald-100' :
                    invite.status === 'revoked' ? 'bg-red-100' : 'bg-slate-100'
                  }`}>
                    {invite.status === 'accepted' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : invite.status === 'revoked' ? (
                      <XCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{invite.email}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-slate-500 text-sm">{ROLE_LABELS[invite.role] || invite.role}</span>
                      <span className="text-slate-300">•</span>
                      <span className="text-slate-400 text-sm">
                        {invite.status === 'accepted' ? `Joined ${formatDate(invite.used_at)}` :
                         invite.status === 'revoked' ? `Revoked ${formatDate(invite.revoked_at)}` :
                         `Expired ${formatDate(invite.expires_at)}`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[invite.status]}`}>
                    {invite.status}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDelete(invite.id)}
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                    title="Delete from history"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && invites.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-heading font-semibold text-slate-900 mb-2">No invites yet</h3>
          <p className="text-slate-500 text-sm mb-6">Invite team members to collaborate on your workspace</p>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#1e3a5f] hover:bg-[#162c46]"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Send First Invite
          </Button>
        </div>
      )}
    </div>
  );
};

export default InviteManagement;
